import React from 'react';
import Layout from '../components/common/Layout';
import FamilyContextBanner from '../components/common/FamilyContextBanner';
import { useFamily } from '../contexts/FamilyContext';
import { useDashboardData } from '../hooks/useDashboardData';
import { getDashboardState } from '../utils/dashboardState';
import FreshStartState from '../components/dashboard/states/FreshStartState';
import AudienceReadyState from '../components/dashboard/states/AudienceReadyState';
import LonelyContentState from '../components/dashboard/states/LonelyContentState';
import ActiveState from '../components/dashboard/states/ActiveState';

/**
 * SeniorDashboard Page
 *
 * Main dashboard that adapts based on user's onboarding state
 *
 * 4 States:
 * 1. Fresh Start: No connections, no stories → Invite family first
 * 2. Audience Ready: Has connections, no stories → Share first story
 * 3. Lonely Content: No connections, has stories → URGENT: Invite family
 * 4. Active: Has both → Full engagement dashboard
 */
function SeniorDashboard() {
  const { activeFamilyId } = useFamily();
  // Fetch real data from API, re-fetch when active family changes
  const { data, loading, error } = useDashboardData(false, activeFamilyId);

  if (loading) {
    return (
      <Layout>
        <DashboardSkeleton />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <DashboardError error={error} />
      </Layout>
    );
  }

  // Determine which state to show based on connections and stories
  const state = getDashboardState({
    connectionCount: data.stats.connectionCount,
    storyCount: data.stats.storyCount
  });

  // Render appropriate state component wrapped in Layout
  let content;
  switch (state) {
    case 'fresh':
      content = <FreshStartState user={data.user} />;
      break;

    case 'audience':
      content = (
        <AudienceReadyState
          user={data.user}
          connections={data.connections}
          questions={data.questions}
        />
      );
      break;

    case 'lonely':
      content = (
        <LonelyContentState
          user={data.user}
          stories={data.stories}
        />
      );
      break;

    case 'active':
      content = <ActiveState data={data} />;
      break;

    default:
      content = <FreshStartState user={data.user} />;
  }

  return (
    <Layout>
      <FamilyContextBanner />
      {content}
    </Layout>
  );
}

/**
 * Loading Skeleton
 */
function DashboardSkeleton() {
  return (
    <div className="min-h-screen bg-[#F5F5DC] flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-[#556B2F] border-t-transparent mb-4"></div>
        <p className="text-xl text-gray-600 font-semibold">
          Loading your dashboard...
        </p>
      </div>
    </div>
  );
}

/**
 * Error Display
 */
function DashboardError({ error }) {
  return (
    <div className="min-h-screen bg-[#F5F5DC] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl p-8 max-w-md shadow-lg">
        <div className="text-6xl text-center mb-4">😕</div>
        <h2 className="text-2xl font-bold text-gray-800 mb-3 text-center">
          Oops! Something went wrong
        </h2>
        <p className="text-gray-600 mb-5 text-center">
          {error.message || 'We couldn\'t load your dashboard. Please try refreshing the page.'}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="w-full bg-[#556B2F] text-white py-3 px-6 rounded-xl font-bold hover:bg-[#4A5D23] transition-colors"
        >
          Refresh Page
        </button>
      </div>
    </div>
  );
}

export default SeniorDashboard;
